/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LightgalleryProvider } from 'react-lightgallery';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import ProgressiveImage from 'react-progressive-graceful-image';
import queryString from 'query-string';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';

import Spinner from '../../../components/loader/spinner';
import FormBuilder from '../../../helper/formBuilder';
import Button from '../../../components/button';

import { fetchBibsDetails, updateBibsCollection } from '../../../services/apis/business/bibs';
import { notificationError } from '../../../redux/saga/notification/actions';

import { INPUT_FIELD, RADIO_GROUP_FIELD } from '../../../constants/form/types';
import { FIELD_REQUIRED, INVALID_PHONE_NUMBER } from '../../../constants/validation/errors';

const CollectionSchema = {
  YupSchema: yup.object().shape({
    collectedByName: yup.string().required(FIELD_REQUIRED),
    collectedByPhone: yup
      .string()
      .required(FIELD_REQUIRED)
      .test('phone-validate', null, function phoneValidation(value) {
        if (value && value.length > 1) {
          const number = parsePhoneNumberFromString(value, 'IN')?.formatInternational() || '';
          if (parsePhoneNumberFromString(number)?.isValid()) {
            return true;
          }
          return this.createError({
            path: this.path,
            message: INVALID_PHONE_NUMBER,
          });
        }
        return false;
      }),
    collectionMethod: yup.string().required(FIELD_REQUIRED),
  }),
  DefaultValues: {
    collectedByName: '',
    collectedByPhone: '',
    collectionMethod: '',
  },
  Template: {
    fields: [
      {
        type: INPUT_FIELD,
        name: 'collectedByName',
        label: 'Collected By Name',
        required: true,
      },
      {
        type: INPUT_FIELD,
        name: 'collectedByPhone',
        label: 'Collected By Phone Number',
        required: true,
      },
      {
        type: RADIO_GROUP_FIELD,
        name: 'collectionMethod',
        label: 'Collection Method',
        column: true,
        required: true,
        options: [
          {
            id: 'SELF',
            name: 'SELF',
          },
          {
            id: 'AUTHORIZED_INDIVIDUAL',
            name: 'AUTHORIZED INDIVIDUAL',
          },
        ],
      },
    ],
  },
};

function BibsDetails() {
  const location = useLocation();
  const dispatch = useDispatch();

  const [race, setRace] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: CollectionSchema.DefaultValues,
    resolver: yupResolver(CollectionSchema.YupSchema),
  });

  const selectedCollectionMethod = watch('collectionMethod');

  const handleCollectionMethodChange = useCallback(() => {
    if (selectedCollectionMethod === 'SELF' && details) {
      setValue(
        'collectedByName',
        `${details.firstName || ''} ${details.lastName || ''}`.trim() || details.name || '',
      );
      setValue('collectedByPhone', details.mobile || '');
    } else if (selectedCollectionMethod === 'AUTHORIZED INDIVIDUAL') {
      const { collectedBy } = details;

      setValue('collectedByName', collectedBy.name || '');
      setValue('collectedByPhone', collectedBy.phone || '');
    }
  }, [selectedCollectionMethod, details, setValue]);

  useEffect(() => {
    if (selectedCollectionMethod && details) {
      handleCollectionMethodChange();
    }
  }, [selectedCollectionMethod, handleCollectionMethodChange]);

  const onCollectionSubmit = useCallback(
    async data => {
      try {
        setSubmitting(true);

        const collectionData = {
          collected: true,
          type: data.collectionMethod === 'SELF' ? 'SELF' : 'AUTHORIZED_INDIVIDUAL',
          name: data.collectedByName,
          phone: data.collectedByPhone,
        };

        const response = await updateBibsCollection(details.id, collectionData);

        setDetails(prevDetails => ({
          ...prevDetails,
          collectedBy: response.collectedBy,
        }));
      } catch (error) {
        dispatch(notificationError('Failed to update collection status. Please try again.'));
      } finally {
        setSubmitting(false);
      }
    },
    [details?.id, dispatch],
  );

  useEffect(() => {
    if (details && details.collectedBy) {
      const { collectedBy } = details;

      setValue('collectedByName', collectedBy.name || '');
      setValue('collectedByPhone', collectedBy.phone || '');

      if (collectedBy.type === 'SELF') {
        setValue('collectionMethod', 'SELF');
      } else if (collectedBy.type === 'AUTHORIZED_INDIVIDUAL') {
        setValue('collectionMethod', 'AUTHORIZED INDIVIDUAL');
      }
    } else if (details && !details.collectedBy) {
      setValue('collectionMethod', 'SELF');
    }
  }, [details, setValue]);

  const resultDetails = useCallback(async (raceID, event, bibNo, name) => {
    const response = await fetchBibsDetails(raceID, event, bibNo, name);

    const { race: responseRace, details: responseDetails } = response;

    setLoading(false);
    setRace(responseRace);
    setDetails(responseDetails);
  }, []);

  useEffect(() => {
    const { raceID, event, bibNo, name } = queryString.parse(location.search);

    resultDetails(raceID, event, bibNo, name).catch(error => {
      setLoading(false);

      throw error;
    });
  }, [resultDetails]);

  if (loading) {
    return (
      <div className="min-height flex items-center">
        <Spinner color="black" size="large" />
      </div>
    );
  }

  return (
    <LightgalleryProvider>
      <Helmet>
        <title>
          {details ? `Bibs Distribution | ${details?.name} | ${details?.bibNo}` : 'Loading...'}{' '}
        </title>
      </Helmet>
      <div className="container max-w-4xl mx-auto px-4 pt-10 pb-10">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <div className="flex flex-col items-center justify-center py-6 px-2 bg-white shadow-lg rounded-md border border-gray-300">
              {race.logo && (
                <div className="mb-3">
                  <div className="shadow-lg">
                    <ProgressiveImage
                      delay={1000}
                      src={race.logo}
                      placeholder={`${race.logo}?tr=bl-10`}>
                      {src => <img src={src} alt={`${race.name}'s logo`} />}
                    </ProgressiveImage>
                  </div>
                </div>
              )}
              <p className="text-lg text-center">
                <strong>{race.name}</strong>
              </p>
              <p className="text-lg text-center">
                <strong>{race.date}</strong>
              </p>
              <p className="text-base text-center mt-2">
                Event: <strong>{race.event}</strong>
              </p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className="bg-white shadow-lg rounded-md border border-gray-300 py-5">
              <p className="text-lg text-center">
                <strong>{details.name}</strong>
              </p>
              {details.gender && (
                <p className="text-base text-center mt-2">
                  GENDER:{' '}
                  <strong>
                    {(() => {
                      if (details.gender === 'M') return 'MALE';
                      if (details.gender === 'F') return 'FEMALE';
                      return details.gender;
                    })()}
                  </strong>
                </p>
              )}
              <p className="text-base text-center mt-2">
                BIB: <strong>#{details.bibNo}</strong>
              </p>
              <p className="text-base text-center mt-2">
                AGE: <strong>{details.age || '-'}</strong>
              </p>
            </div>
            <div className="grid grid-cols-12 gap-3 mt-5">
              <div className="col-span-12 bg-white shadow-lg rounded-md border border-gray-300 py-4">
                <div className="flex">
                  <div className="w-full p-3">
                    <p className="text-sm text-center font-semibold uppercase">T-Shirt</p>
                    <p className="text-center text-xl my-1">
                      <strong>{details.tShirtSize}</strong>
                    </p>
                  </div>
                  <div className="w-full p-3">
                    <p className="text-sm text-center font-semibold uppercase">Blood Group</p>
                    <p className="text-center text-xl my-1">
                      <strong>{details.bloodGroup || '-'}</strong>
                    </p>
                  </div>
                  <div className="w-full p-3">
                    <p className="text-sm text-center font-semibold uppercase">DOB</p>
                    <p className="text-center text-xl my-1">
                      <strong>{details.birthDate}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 bg-white shadow-lg rounded-md border border-gray-300 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">Bib Collection Details</h3>
                {details.collectedBy && (
                  <div className="text-right">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        details.collectedBy.collected
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {details.collectedBy.collected ? 'Collected' : 'Not Collected'}
                    </span>
                    {details.collectedBy.collected && details.collectedBy.collectedAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Collected: {new Date(details.collectedBy.collectedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <form onSubmit={handleSubmit(onCollectionSubmit)}>
                <div className="grid grid-cols-12 gap-4">
                  {FormBuilder({
                    template: {
                      ...CollectionSchema.Template,
                      fields: CollectionSchema.Template.fields.map(field => ({
                        ...field,
                        disabled: submitting,
                      })),
                    },
                    control,
                  }).map(({ field }, index) => (
                    <div key={index} className="col-span-12 sm:col-span-6">
                      {field}
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end">
                  <Button label="Update" loading={submitting} />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </LightgalleryProvider>
  );
}

export default BibsDetails;
